import { z } from 'zod';
import { DateString, JsonObject, Timestamp, Uuid } from './common.js';
import { Analysis } from './analyses.js';
import { Fact } from './facts.js';
import { FactStatus, FactType, SourceTier } from './vocab.js';

/**
 * `GET /subjects/:id/brief` — the deliverable render (server-design.md §3.1).
 *
 * Narrow by design: analyses by kind, the supporting facts with their lineage
 * refs, and the queued (non-blocking) adjudication rows inline. Everything else
 * the Research tab shows — identity block, pending `identity_confirm`, run
 * history, the decision log — comes from the record read.
 *
 * One thing it is **not** narrow about any more: which run the analyses came
 * from. While a run is live the brief pins itself to the dossier as it stood
 * before that run wrote anything, and says so in `view` (§3e option b, decided
 * 2026-08-31) — a dossier that rewrites itself under the reader mid-run does not
 * read as a record. `?view=draft` asks for the live one explicitly.
 */
export const BRIEF_VIEWS = ['current', 'stable', 'draft'] as const;
export const BriefView = z.enum(BRIEF_VIEWS);
export type BriefView = z.infer<typeof BriefView>;

/**
 * `GET /subjects/:id/brief?view=draft` — the only query this read takes. Absent
 * means "let the server decide": `current` with no live run, `stable` with one.
 * `draft` is the reader asking for the live run's in-progress writes anyway.
 */
export const BriefQuery = z
  .object({
    view: z.literal('draft').optional(),
  })
  .strict();
export type BriefQuery = z.infer<typeof BriefQuery>;
/**
 * One open question on this brief — **every** pending non-blocking
 * `claim_adjudication` for the subject, not only the ones with a row to sit on.
 *
 * It used to be the pair `{decision_id, lineage_id}` and nothing else, because
 * the only place a queued row rendered was inline at its fact. Two shapes that
 * carries badly, both seen in the first prod dossier:
 *
 *  - the row's lineage is **active but on another entity's record**. The page's
 *    fact list is `subject_entity_id = :id OR counterparty_entity_id = :id`, so
 *    a pair fact whose canonical subject is the counterparty renders inline
 *    perfectly well; what does not is a lineage with the dossier subject in
 *    **neither** slot — a duplicate person entity minted mid-run holding the
 *    subject slot (the observed prod shape), or an org-principal fact subjected
 *    to the **org** under §7.2c with the person nowhere on it;
 *  - the row carries **no lineage at all**, or one that a later run negated
 *    (§3g look-alike re-gating), which the inline rule also refuses to draw.
 *
 * Either way the home inbox counted a question the page would not show and the
 * rewrite stayed refused: a deadlock with no surface. So the row now carries
 * enough to render standalone — the finding's own fields where a lineage exists,
 * the decision payload's excerpt where it does not — and the page renders the
 * complement of its inline set in a "Flagged findings" section. Inline set ∪
 * that section = every row here, which is the invariant that keeps the home
 * count honest.
 */
export const BriefQueuedRow = z.object({
  decision_id: Uuid,
  /**
   * The fact the row is attached to, when there is one. Non-null does **not**
   * mean the fact is on this dossier: it may be on another entity's record, or
   * negated. Null is a blocking-shaped row that never materialized a fact
   * (hand-written gates, and rows from vintages that stamped no lineage).
   */
  lineage_id: Uuid.nullable(),
  /** The run that raised the question; null for a row that belongs to no run. */
  run_id: Uuid.nullable(),
  /** From the decision row, falling back to its payload. Null only if neither said. */
  fact_type: FactType.nullable(),
  created_at: Timestamp,

  /* —— from the decision payload; every field varies by vintage —— */
  evidence_excerpt: z.string().nullable(),
  source_tier: SourceTier.nullable(),
  content_date: DateString.nullable(),

  /* —— from the lineage, looked up **org-scoped and not subject-scoped** —— */
  /**
   * The lineage's own status. `negated` is the case the section says a sentence
   * about: keeping it answers the question without putting the finding back.
   */
  lineage_status: FactStatus.nullable(),
  /** The current version's `fields`, so the section can state the finding itself. */
  fields: JsonObject.nullable(),
  /** The counterparty entity's display name, when the lineage names one. */
  counterparty_name: z.string().nullable(),
  /**
   * The counterparty **column**, paired with `fact_subject_id` so the row can be
   * rendered the way a fact row is: which side of a pair fact the sentence is
   * written from is an identity question, answered off ids (`pair-side.ts`) and
   * never off the spelling in `fields`.
   */
  counterparty_entity_id: Uuid.nullable(),
  /**
   * **Whose record the fact is on** — `fact.subject_entity_id`, which is not
   * always this dossier's subject and is the whole reason the row needs a
   * surface of its own.
   *
   * The reader's-direction render is subject-implicit ("Married to X"), so a
   * sentence written for the wrong owner is a false statement on a deliverable:
   * the client renders these against `fact_subject_name` and says whose record
   * it is. It also governs what leaving the finding out means — the negation
   * lands on that person's record, not on this one.
   */
  fact_subject_id: Uuid.nullable(),
  /** Null when that entity has been deleted; the row still renders and answers. */
  fact_subject_name: z.string().nullable(),
});
export type BriefQueuedRow = z.infer<typeof BriefQueuedRow>;

export const SubjectBrief = z.object({
  analyses: z.array(Analysis),
  facts: z.array(Fact),
  /**
   * The open questions on this brief. On a `stable` view the live run's own
   * rows are **withheld** — they batch at the run's `awaiting_adjudication`
   * park instead of drifting onto the pinned page one at a time — while rows
   * from prior runs stay answerable here as they always were. `draft` and
   * `current` carry everything pending.
   *
   * Not "the rows to render inline" any more: rows whose fact is on this
   * dossier render inline at that fact, and every remaining row renders in the
   * page's Flagged findings section. Nothing here is unrenderable.
   */
  queued: z.array(BriefQueuedRow),
  /**
   * Gated facts past the per-run queued budget (default 20). They materialized
   * and are counted, but no row was queued for them: "N further findings were
   * flagged but not queued for review." Zero when the budget was never hit.
   *
   * Scoped the same way `queued` is: a `stable` view does not count the live
   * run's collapsed rows, because they are not findings on the dossier it is
   * showing.
   */
  queued_overflow_count: z.number().int().nonnegative(),

  /**
   * Which dossier these analyses are:
   *
   *  - `current` — no run is live, so this is simply the record as it stands
   *    (the unchanged world; nothing to label);
   *  - `stable` — a run is live and the analyses are pinned to the dossier as it
   *    was before that run wrote anything;
   *  - `draft` — the live run's in-progress writes, asked for by `?view=draft`,
   *    or served on its own when a live run is the *only* run this subject has
   *    ever had (there is no finished dossier to pin to).
   */
  view: BriefView,
  /**
   * The last completed research run, for the "as of <date>" label on a `stable`
   * view. Null on `current` and `draft`, and null on a `stable` view whose prior
   * rows were hand-written rather than produced by a run.
   *
   * It labels the view; it does not *select* it. Selection excludes the live
   * run's rows rather than filtering to this run's — see the query comment in
   * `AnalysesService.stableForSubject` for why that is the safer rule.
   */
  as_of_run_id: Uuid.nullable(),
  as_of_completed_at: Timestamp.nullable(),
  /**
   * Non-null while a run is live for this subject — which is exactly when the
   * draft exists to toggle to. Null on `current`.
   */
  live_run_id: Uuid.nullable(),
});
export type SubjectBrief = z.infer<typeof SubjectBrief>;
